export const calculusICourse = {
  id: 'calculus-1-101',
  title: 'Calculus I',
  description: 'A comprehensive introduction to differential calculus covering limits, derivatives, and their applications. This course provides the mathematical foundation essential for computer science, engineering, and advanced programming concepts.',
  credits: 4,
  duration: '15 weeks',
  level: 'Intermediate',
  category: 'Coding',
  learningObjectives: [
    'Master the concept of limits and their role in calculus',
    'Understand and compute derivatives using various techniques',
    'Apply derivatives to solve optimization and related rates problems',
    'Analyze functions using calculus concepts including continuity and differentiability',
    'Connect calculus concepts to computer science applications and algorithms',
    'Develop mathematical reasoning and proof-writing skills',
    'Use calculus in modeling real-world problems relevant to programming',
    'Prepare for advanced mathematics required in computer science'
  ],
  prerequisites: ['Pre-calculus or equivalent', 'Trigonometry', 'Strong algebra skills'],
  skills: [
    'Limit Calculation',
    'Derivative Computation',
    'Function Analysis',
    'Optimization Techniques',
    'Mathematical Modeling',
    'Problem-Solving Strategies',
    'Mathematical Proof Writing',
    'Graphical Analysis',
    'Algorithmic Thinking',
    'Numerical Methods',
    'Mathematical Software Usage',
    'Abstract Reasoning'
  ],
  marketDemand: 'High',
  averageSalary: '$70,000 - $140,000',
  careerPaths: [
    'Software Engineer',
    'Data Scientist',
    'Machine Learning Engineer',
    'Computer Graphics Developer',
    'Quantitative Analyst',
    'Research Scientist',
    'Algorithm Developer',
    'Systems Engineer',
    'Computational Scientist',
    'Robotics Engineer',
    'Game Developer',
    'Financial Technology Developer'
  ],
  industryApplications: [
    'Machine Learning and AI',
    'Computer Graphics and Animation',
    'Scientific Computing',
    'Financial Modeling',
    'Optimization Algorithms',
    'Signal Processing',
    'Game Physics',
    'Data Analysis and Visualization'
  ],
  certifications: [
    'Mathematical Association of America Certifications',
    'Data Science Professional Certificates',
    'Machine Learning Specializations',
    'Quantitative Analysis Certifications',
    'Scientific Computing Credentials'
  ],
  modules: [
    {
      id: 'module-1',
      title: 'Limits and Continuity',
      description: 'Introduction to the fundamental concept of limits and continuous functions as the foundation of calculus',
      weeks: [1, 2, 3],
      learningOutcomes: [
        'Understand the intuitive and formal definition of limits',
        'Calculate limits using algebraic and graphical methods',
        'Identify and resolve indeterminate forms',
        'Apply the concept of continuity to analyze functions'
      ],
      content: [
        {
          id: 'lesson-1',
          title: 'Introduction to Limits',
          content: 'Explore the fundamental concept of limits as the foundation of calculus. This lesson introduces the intuitive idea of a limit through graphical and numerical approaches, leading to the formal epsilon-delta definition. You will learn how limits describe the behavior of functions as input values approach specific points, setting the stage for derivatives and integrals.',
          duration: '120 minutes',
          readingMaterials: [
            'Calculus: Early Transcendentals - Chapter 2.1-2.2',
            'Understanding Limits Through Examples',
            'Graphical and Numerical Approaches to Limits'
          ],
          keyTerms: [
            'Limit',
            'Approaching a Value',
            'Left-hand Limit',
            'Right-hand Limit',
            'One-sided Limits',
            'Epsilon-Delta Definition',
            'Indeterminate Form',
            'Limit Laws'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Create a function that numerically approximates the limit of (x² - 1)/(x - 1) as x approaches 1. Test values approaching 1 from both sides and display the results.',
              initialCode: '// Numerical limit approximation\n// Function: (x² - 1)/(x - 1) as x approaches 1\n// Test values like 0.9, 0.99, 0.999 and 1.1, 1.01, 1.001\n\nfunction f(x) {\n    return (x * x - 1) / (x - 1);\n}\n\n// Test values approaching 1\nlet leftValues = [0.9, 0.99, 0.999];\nlet rightValues = [1.1, 1.01, 1.001];\n\n// Calculate and display results\nconsole.log("Approaching from left:");\nfor (let x of leftValues) {\n    console.log(`f(${x}) = ${f(x)}`);\n}\n\nconsole.log("Approaching from right:");\nfor (let x of rightValues) {\n    console.log(`f(${x}) = ${f(x)}`);\n}',
              expectedOutput: 'Approaching from left:\nf(0.9) = 1.9\nf(0.99) = 1.99\nf(0.999) = 1.999\nApproaching from right:\nf(1.1) = 2.1\nf(1.01) = 2.01\nf(1.001) = 2.001',
              hints: [
                'The function is already defined correctly',
                'Use the provided arrays of test values',
                'Loop through each array and calculate f(x)',
                'The limit appears to be approaching 2'
              ]
            }
          }
        },
        {
          id: 'lesson-2',
          title: 'Limit Laws and Techniques',
          content: 'Master the fundamental limit laws that allow you to calculate limits algebraically. This lesson covers the sum, product, and quotient rules for limits, along with techniques for evaluating limits including factoring, rationalizing, and using special limit forms. You will learn to handle indeterminate forms systematically.',
          duration: '120 minutes',
          readingMaterials: [
            'Limit Laws and Properties',
            'Techniques for Evaluating Limits',
            'Special Limits and Important Forms'
          ],
          keyTerms: [
            'Limit Laws',
            'Sum Rule',
            'Product Rule',
            'Quotient Rule',
            'Factoring Method',
            'Rationalization',
            'Squeeze Theorem',
            'Special Limits'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Create a function that calculates the limit of (x² + 3x + 2)/(x + 1) as x approaches -1 using algebraic simplification. Factor the numerator and simplify before substitution.',
              initialCode: '// Algebraic limit calculation\n// Function: (x² + 3x + 2)/(x + 1) as x approaches -1\n// Factor and simplify first\n\nfunction calculateLimit() {\n    // Original function: (x² + 3x + 2)/(x + 1)\n    // Factor numerator: (x + 1)(x + 2)\n    // Simplify: x + 2 (after canceling x + 1)\n    // Substitute x = -1 into simplified form\n    \n    let x = -1;\n    let result = x + 2;\n    return result;\n}\n\nlet limit = calculateLimit();\nconsole.log(`The limit is: ${limit}`);\n\n// Verify by testing values near -1\nfunction originalFunction(x) {\n    return (x * x + 3 * x + 2) / (x + 1);\n}\n\nlet testValues = [-1.1, -1.01, -1.001, -0.999, -0.99, -0.9];\nconsole.log("Verification with test values:");\nfor (let x of testValues) {\n    console.log(`f(${x}) = ${originalFunction(x).toFixed(6)}`);\n}',
              expectedOutput: 'The limit is: 1\nVerification with test values:\nf(-1.1) = 0.900000\nf(-1.01) = 0.990000\nf(-1.001) = 0.999000\nf(-0.999) = 1.001000\nf(-0.99) = 1.010000\nf(-0.9) = 1.100000',
              hints: [
                'Factor the numerator x² + 3x + 2 = (x + 1)(x + 2)',
                'Cancel the common factor (x + 1)',
                'The simplified form is x + 2',
                'Substitute x = -1 to get the limit value of 1'
              ]
            }
          }
        },
        {
          id: 'lesson-3',
          title: 'Continuity and Discontinuities',
          content: 'Understand the concept of continuity and learn to classify different types of discontinuities. This lesson explores continuous functions, the definition of continuity at a point, and how to identify removable discontinuities, jump discontinuities, and infinite discontinuities. You will also learn about the Intermediate Value Theorem.',
          duration: '120 minutes',
          readingMaterials: [
            'Continuity and Continuous Functions',
            'Types of Discontinuities',
            'Intermediate Value Theorem and Applications'
          ],
          keyTerms: [
            'Continuity',
            'Continuous Function',
            'Discontinuity',
            'Removable Discontinuity',
            'Jump Discontinuity',
            'Infinite Discontinuity',
            'Intermediate Value Theorem',
            'Piecewise Functions'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Create a function that checks if a piecewise function is continuous at x = 2. The function is f(x) = x² for x < 2, and f(x) = 4x - 4 for x ≥ 2.',
              initialCode: '// Continuity checker for piecewise function\n// f(x) = x² for x < 2\n// f(x) = 4x - 4 for x ≥ 2\n// Check continuity at x = 2\n\nfunction piecewiseFunction(x) {\n    if (x < 2) {\n        return x * x;\n    } else {\n        return 4 * x - 4;\n    }\n}\n\nfunction checkContinuityAt2() {\n    // Check three conditions for continuity:\n    // 1. f(2) exists\n    let f2 = piecewiseFunction(2);\n    \n    // 2. Left-hand limit as x approaches 2\n    let leftLimit = 2 * 2; // Using x² formula\n    \n    // 3. Right-hand limit as x approaches 2\n    let rightLimit = 4 * 2 - 4; // Using 4x - 4 formula\n    \n    console.log(`f(2) = ${f2}`);\n    console.log(`Left-hand limit = ${leftLimit}`);\n    console.log(`Right-hand limit = ${rightLimit}`);\n    \n    if (f2 === leftLimit && leftLimit === rightLimit) {\n        console.log("The function is continuous at x = 2");\n    } else {\n        console.log("The function is NOT continuous at x = 2");\n    }\n}\n\ncheckContinuityAt2();',
              expectedOutput: 'f(2) = 4\nLeft-hand limit = 4\nRight-hand limit = 4\nThe function is continuous at x = 2',
              hints: [
                'For x < 2, use f(x) = x²',
                'For x ≥ 2, use f(x) = 4x - 4',
                'Left-hand limit uses the first piece: 2² = 4',
                'Right-hand limit uses the second piece: 4(2) - 4 = 4'
              ]
            }
          }
        },
        {
          id: 'lesson-4',
          title: 'Limits at Infinity and Infinite Limits',
          content: 'Explore limits involving infinity, including limits at infinity and infinite limits. This lesson covers horizontal and vertical asymptotes, end behavior of functions, and techniques for evaluating limits involving infinity. You will learn to analyze rational functions and their asymptotic behavior.',
          duration: '90 minutes',
          readingMaterials: [
            'Limits at Infinity',
            'Infinite Limits and Asymptotes',
            'End Behavior of Functions'
          ],
          keyTerms: [
            'Limit at Infinity',
            'Infinite Limit',
            'Horizontal Asymptote',
            'Vertical Asymptote',
            'End Behavior',
            'Rational Function Analysis',
            'Dominant Terms'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Analyze the end behavior of f(x) = (3x² + 5x + 1)/(2x² + x - 3) by calculating the limit as x approaches positive and negative infinity.',
              initialCode: '// End behavior analysis\n// f(x) = (3x² + 5x + 1)/(2x² + x - 3)\n// Find limit as x → ±∞\n\nfunction analyzeEndBehavior() {\n    // For rational functions, divide by highest power in denominator\n    // (3x² + 5x + 1)/(2x² + x - 3) = (3 + 5/x + 1/x²)/(2 + 1/x - 3/x²)\n    // As x → ±∞, terms with x in denominator approach 0\n    \n    let limitAsXToInfinity = 3 / 2; // Ratio of leading coefficients\n    \n    console.log(`As x → +∞, f(x) → ${limitAsXToInfinity}`);\n    console.log(`As x → -∞, f(x) → ${limitAsXToInfinity}`);\n    console.log(`Horizontal asymptote: y = ${limitAsXToInfinity}`);\n    \n    // Verify with large values\n    function f(x) {\n        return (3*x*x + 5*x + 1) / (2*x*x + x - 3);\n    }\n    \n    console.log(\"\\nVerification:\");\n    let largeValues = [100, 1000, 10000, -100, -1000, -10000];\n    for (let x of largeValues) {\n        console.log(`f(${x}) = ${f(x).toFixed(6)}`);\n    }\n}\n\nanalyzeEndBehavior();',
              expectedOutput: 'As x → +∞, f(x) → 1.5\nAs x → -∞, f(x) → 1.5\nHorizontal asymptote: y = 1.5\n\nVerification:\nf(100) = 1.492537\nf(1000) = 1.499250\nf(10000) = 1.499925\nf(-100) = 1.507537\nf(-1000) = 1.500750\nf(-10000) = 1.500075',
              hints: [
                'For rational functions, the horizontal asymptote is the ratio of leading coefficients',
                'Leading coefficient of numerator: 3',
                'Leading coefficient of denominator: 2',
                'Therefore, horizontal asymptote is y = 3/2 = 1.5'
              ]
            }
          }
        }
      ],
      assessments: [
        {
          type: 'Problem Set',
          title: 'Limits and Continuity Calculations',
          points: 75,
          dueWeek: 2
        },
        {
          type: 'Quiz',
          title: 'Limit Laws and Techniques',
          points: 50,
          dueWeek: 3
        }
      ]
    },
    {
      id: 'module-2',
      title: 'Introduction to Derivatives',
      description: 'Develop understanding of derivatives as rates of change and slopes of tangent lines, including the formal definition and basic differentiation rules',
      weeks: [4, 5, 6],
      learningOutcomes: [
        'Understand the derivative as a limit of difference quotients',
        'Calculate derivatives using the definition and basic rules',
        'Interpret derivatives as rates of change and slopes',
        'Apply differentiation to simple functions and real-world problems'
      ],
      content: [
        {
          id: 'lesson-5',
          title: 'The Derivative as a Limit',
          content: 'Discover the derivative through the fundamental limit definition. This lesson explores how derivatives arise from the concept of instantaneous rate of change and slope of tangent lines. You will learn the formal definition of the derivative as a limit and practice calculating derivatives from first principles.',
          duration: '120 minutes',
          readingMaterials: [
            'The Derivative: Definition and Interpretation',
            'Tangent Lines and Instantaneous Rate of Change',
            'Calculating Derivatives from First Principles'
          ],
          keyTerms: [
            'Derivative',
            'Difference Quotient',
            'Instantaneous Rate of Change',
            'Tangent Line',
            'Secant Line',
            'Average Rate of Change',
            'Differentiability',
            'First Principles'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Calculate the derivative of f(x) = x² at x = 3 using the limit definition. Use increasingly small values of h to approximate the limit.',
              initialCode: '// Derivative using limit definition\n// f(x) = x², find f\'(3)\n// Definition: f\'(a) = lim(h→0) [f(a+h) - f(a)] / h\n\nfunction f(x) {\n    return x * x;\n}\n\nfunction derivativeAtPoint(a) {\n    console.log(`Finding derivative of f(x) = x² at x = ${a}`);\n    console.log("Using f\'(a) = lim(h→0) [f(a+h) - f(a)] / h\\n");\n    \n    let hValues = [0.1, 0.01, 0.001, 0.0001, 0.00001];\n    \n    for (let h of hValues) {\n        let numerator = f(a + h) - f(a);\n        let differenceQuotient = numerator / h;\n        console.log(`h = ${h}: [f(${a + h}) - f(${a})] / ${h} = ${differenceQuotient}`);\n    }\n    \n    // The exact derivative of x² is 2x\n    let exactDerivative = 2 * a;\n    console.log(`\\nExact derivative: f\'(${a}) = ${exactDerivative}`);\n    \n    return exactDerivative;\n}\n\nderivativeAtPoint(3);',
              expectedOutput: 'Finding derivative of f(x) = x² at x = 3\nUsing f\'(a) = lim(h→0) [f(a+h) - f(a)] / h\n\nh = 0.1: [f(3.1) - f(3)] / 0.1 = 6.1\nh = 0.01: [f(3.01) - f(3)] / 0.01 = 6.01\nh = 0.001: [f(3.001) - f(3)] / 0.001 = 6.001\nh = 0.0001: [f(3.0001) - f(3)] / 0.0001 = 6.0001\nh = 0.00001: [f(3.00001) - f(3)] / 0.00001 = 6.00001\n\nExact derivative: f\'(3) = 6',
              hints: [
                'Use the limit definition with decreasing values of h',
                'Calculate [f(3+h) - f(3)] / h for each h value',
                'The limit should approach 6 as h approaches 0',
                'The exact derivative of x² is 2x, so at x=3, it\'s 2(3) = 6'
              ]
            }
          }
        },
        {
          id: 'lesson-6',
          title: 'Basic Differentiation Rules',
          content: 'Master the fundamental rules of differentiation that make calculating derivatives efficient. This lesson covers the power rule, constant rule, constant multiple rule, sum and difference rules. You will learn to differentiate polynomial functions quickly and accurately without using the limit definition.',
          duration: '120 minutes',
          readingMaterials: [
            'Basic Differentiation Rules',
            'The Power Rule and Its Applications',
            'Differentiating Polynomial Functions'
          ],
          keyTerms: [
            'Power Rule',
            'Constant Rule',
            'Constant Multiple Rule',
            'Sum Rule',
            'Difference Rule',
            'Polynomial Differentiation',
            'Linearity of Derivatives'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Create a function that calculates the derivative of polynomial functions using the power rule. Test it with f(x) = 3x⁴ - 2x³ + 5x² - 7x + 2.',
              initialCode: '// Polynomial derivative calculator using power rule\n// f(x) = 3x⁴ - 2x³ + 5x² - 7x + 2\n// f\'(x) = 12x³ - 6x² + 10x - 7\n\nfunction polynomialDerivative() {\n    // Original: f(x) = 3x⁴ - 2x³ + 5x² - 7x + 2\n    // Apply power rule: d/dx(axⁿ) = n·a·x^(n-1)\n    \n    console.log("Original function: f(x) = 3x⁴ - 2x³ + 5x² - 7x + 2");\n    console.log("\\nApplying power rule to each term:");\n    console.log("d/dx(3x⁴) = 4·3·x³ = 12x³");\n    console.log("d/dx(-2x³) = 3·(-2)·x² = -6x²");\n    console.log("d/dx(5x²) = 2·5·x¹ = 10x");\n    console.log("d/dx(-7x) = 1·(-7)·x⁰ = -7");\n    console.log("d/dx(2) = 0 (constant)");\n    \n    console.log("\\nDerivative: f\'(x) = 12x³ - 6x² + 10x - 7");\n    \n    // Test at x = 2\n    let x = 2;\n    let derivativeValue = 12*x*x*x - 6*x*x + 10*x - 7;\n    console.log(`\\nAt x = ${x}: f\'(${x}) = ${derivativeValue}`);\n    \n    return derivativeValue;\n}\n\npolynomialDerivative();',
              expectedOutput: 'Original function: f(x) = 3x⁴ - 2x³ + 5x² - 7x + 2\n\nApplying power rule to each term:\nd/dx(3x⁴) = 4·3·x³ = 12x³\nd/dx(-2x³) = 3·(-2)·x² = -6x²\nd/dx(5x²) = 2·5·x¹ = 10x\nd/dx(-7x) = 1·(-7)·x⁰ = -7\nd/dx(2) = 0 (constant)\n\nDerivative: f\'(x) = 12x³ - 6x² + 10x - 7\n\nAt x = 2: f\'(2) = 81',
              hints: [
                'Apply the power rule: d/dx(axⁿ) = n·a·x^(n-1)',
                'For 3x⁴: coefficient is 3, power is 4, so derivative is 4·3·x³ = 12x³',
                'Constants have derivative 0',
                'Combine all terms for the final derivative'
              ]
            }
          }
        },
        {
          id: 'lesson-7',
          title: 'Product and Quotient Rules',
          content: 'Learn advanced differentiation techniques for products and quotients of functions. This lesson covers the product rule and quotient rule, essential tools for differentiating more complex functions. You will practice applying these rules and learn when each is most appropriate.',
          duration: '120 minutes',
          readingMaterials: [
            'Product Rule for Derivatives',
            'Quotient Rule for Derivatives',
            'Applications of Product and Quotient Rules'
          ],
          keyTerms: [
            'Product Rule',
            'Quotient Rule',
            'Function Composition',
            'Differentiation Techniques',
            'Complex Functions',
            'Rule Selection Strategy'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Use the product rule to find the derivative of f(x) = (2x + 1)(3x² - 4). Show each step of the product rule application.',
              initialCode: '// Product rule application\n// f(x) = (2x + 1)(3x² - 4)\n// Product rule: (uv)\' = u\'v + uv\'\n\nfunction productRuleExample() {\n    console.log("Finding derivative of f(x) = (2x + 1)(3x² - 4)");\n    console.log("Using product rule: (uv)\' = u\'v + uv\'\\n");\n    \n    // Identify u and v\n    console.log("Let u = 2x + 1");\n    console.log("Let v = 3x² - 4\\n");\n    \n    // Find derivatives\n    console.log("u\' = d/dx(2x + 1) = 2");\n    console.log("v\' = d/dx(3x² - 4) = 6x\\n");\n    \n    // Apply product rule\n    console.log("f\'(x) = u\'v + uv\'");\n    console.log("f\'(x) = (2)(3x² - 4) + (2x + 1)(6x)");\n    console.log("f\'(x) = 6x² - 8 + 12x² + 6x");\n    console.log("f\'(x) = 18x² + 6x - 8");\n    \n    // Verify by expanding first, then differentiating\n    console.log("\\nVerification by expanding first:");\n    console.log("f(x) = (2x + 1)(3x² - 4) = 6x³ - 8x + 3x² - 4 = 6x³ + 3x² - 8x - 4");\n    console.log("f\'(x) = 18x² + 6x - 8 ✓");\n    \n    // Test at x = 1\n    let x = 1;\n    let result = 18*x*x + 6*x - 8;\n    console.log(`\\nAt x = ${x}: f\'(${x}) = ${result}`);\n    \n    return result;\n}\n\nproductRuleExample();',
              expectedOutput: 'Finding derivative of f(x) = (2x + 1)(3x² - 4)\nUsing product rule: (uv)\' = u\'v + uv\'\n\nLet u = 2x + 1\nLet v = 3x² - 4\n\nu\' = d/dx(2x + 1) = 2\nv\' = d/dx(3x² - 4) = 6x\n\nf\'(x) = u\'v + uv\'\nf\'(x) = (2)(3x² - 4) + (2x + 1)(6x)\nf\'(x) = 6x² - 8 + 12x² + 6x\nf\'(x) = 18x² + 6x - 8\n\nVerification by expanding first:\nf(x) = (2x + 1)(3x² - 4) = 6x³ - 8x + 3x² - 4 = 6x³ + 3x² - 8x - 4\nf\'(x) = 18x² + 6x - 8 ✓\n\nAt x = 1: f\'(1) = 16',
              hints: [
                'Identify u = 2x + 1 and v = 3x² - 4',
                'Find u\' = 2 and v\' = 6x',
                'Apply product rule: (uv)\' = u\'v + uv\'',
                'Simplify: 6x² - 8 + 12x² + 6x = 18x² + 6x - 8'
              ]
            }
          }
        },
        {
          id: 'lesson-8',
          title: 'Chain Rule Introduction',
          content: 'Introduction to the chain rule for differentiating composite functions. This lesson covers the basic form of the chain rule and its application to functions within functions. You will learn to identify composite functions and apply the chain rule systematically.',
          duration: '120 minutes',
          readingMaterials: [
            'Chain Rule for Composite Functions',
            'Identifying Composite Functions',
            'Chain Rule Applications and Examples'
          ],
          keyTerms: [
            'Chain Rule',
            'Composite Function',
            'Outer Function',
            'Inner Function',
            'Function Composition',
            'Nested Functions',
            'Differentiation Strategy'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Use the chain rule to find the derivative of f(x) = (3x + 2)⁵. Identify the outer and inner functions and apply the chain rule.',
              initialCode: '// Chain rule application\n// f(x) = (3x + 2)⁵\n// Chain rule: d/dx[f(g(x))] = f\'(g(x)) · g\'(x)\n\nfunction chainRuleExample() {\n    console.log("Finding derivative of f(x) = (3x + 2)⁵");\n    console.log("Using chain rule: d/dx[f(g(x))] = f\'(g(x)) · g\'(x)\\n");\n    \n    // Identify composite structure\n    console.log("Identify the composite structure:");\n    console.log("Outer function: f(u) = u⁵ where u = 3x + 2");\n    console.log("Inner function: g(x) = 3x + 2\\n");\n    \n    // Find derivatives\n    console.log("Find derivatives:");\n    console.log("f\'(u) = 5u⁴");\n    console.log("g\'(x) = 3\\n");\n    \n    // Apply chain rule\n    console.log("Apply chain rule:");\n    console.log("d/dx[(3x + 2)⁵] = f\'(g(x)) · g\'(x)");\n    console.log("                = 5(3x + 2)⁴ · 3");\n    console.log("                = 15(3x + 2)⁴");\n    \n    console.log("\\nFinal answer: f\'(x) = 15(3x + 2)⁴");\n    \n    // Test at x = 0\n    let x = 0;\n    let innerValue = 3*x + 2;\n    let result = 15 * Math.pow(innerValue, 4);\n    console.log(`\\nAt x = ${x}: f\'(${x}) = 15(${innerValue})⁴ = ${result}`);\n    \n    return result;\n}\n\nchainRuleExample();',
              expectedOutput: 'Finding derivative of f(x) = (3x + 2)⁵\nUsing chain rule: d/dx[f(g(x))] = f\'(g(x)) · g\'(x)\n\nIdentify the composite structure:\nOuter function: f(u) = u⁵ where u = 3x + 2\nInner function: g(x) = 3x + 2\n\nFind derivatives:\nf\'(u) = 5u⁴\ng\'(x) = 3\n\nApply chain rule:\nd/dx[(3x + 2)⁵] = f\'(g(x)) · g\'(x)\n                = 5(3x + 2)⁴ · 3\n                = 15(3x + 2)⁴\n\nFinal answer: f\'(x) = 15(3x + 2)⁴\n\nAt x = 0: f\'(0) = 15(2)⁴ = 240',
              hints: [
                'Identify outer function as u⁵ and inner function as 3x + 2',
                'Derivative of u⁵ is 5u⁴',
                'Derivative of 3x + 2 is 3',
                'Multiply: 5(3x + 2)⁴ · 3 = 15(3x + 2)⁴'
              ]
            }
          }
        }
      ],
      assessments: [
        {
          type: 'Differentiation Practice Set',
          title: 'Basic Rules and Techniques',
          points: 100,
          dueWeek: 5
        },
        {
          type: 'Application Problems',
          title: 'Derivative Applications in Programming',
          points: 75,
          dueWeek: 6
        }
      ]
    },
    {
      id: 'module-3',
      title: 'Applications of Derivatives',
      description: 'Apply derivative concepts to solve optimization problems, analyze function behavior, and model real-world situations',
      weeks: [7, 8, 9],
      learningOutcomes: [
        'Use derivatives to find critical points and analyze function behavior',
        'Solve optimization problems using calculus techniques',
        'Apply the Mean Value Theorem and related concepts',
        'Model real-world problems using derivatives and rate of change'
      ],
      content: [
        {
          id: 'lesson-9',
          title: 'Critical Points and Function Analysis',
          content: 'Learn to analyze functions using derivatives to find local maxima, minima, and points of inflection. This lesson covers critical points, the first derivative test, the second derivative test, and curve sketching techniques. You will develop skills in complete function analysis.',
          duration: '120 minutes',
          readingMaterials: [
            'Critical Points and Extrema',
            'First and Second Derivative Tests',
            'Curve Sketching with Calculus'
          ],
          keyTerms: [
            'Critical Point',
            'Local Maximum',
            'Local Minimum',
            'First Derivative Test',
            'Second Derivative Test',
            'Inflection Point',
            'Concavity',
            'Function Analysis'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Analyze the function f(x) = x³ - 6x² + 9x + 1. Find critical points and determine their nature using the second derivative test.',
              initialCode: '// Function analysis: f(x) = x³ - 6x² + 9x + 1\n// Find critical points and classify them\n\nfunction analyzeFunction() {\n    console.log("Analyzing f(x) = x³ - 6x² + 9x + 1\\n");\n    \n    // First derivative\n    console.log("Step 1: Find f\'(x)");\n    console.log("f\'(x) = 3x² - 12x + 9");\n    \n    // Critical points: f\'(x) = 0\n    console.log("\\nStep 2: Find critical points by solving f\'(x) = 0");\n    console.log("3x² - 12x + 9 = 0");\n    console.log("3(x² - 4x + 3) = 0");\n    console.log("3(x - 1)(x - 3) = 0");\n    console.log("Critical points: x = 1, x = 3\\n");\n    \n    // Second derivative\n    console.log("Step 3: Find f\'\'(x) for second derivative test");\n    console.log("f\'\'(x) = 6x - 12\\n");\n    \n    // Test critical points\n    console.log("Step 4: Apply second derivative test");\n    \n    let x1 = 1;\n    let f_double_prime_1 = 6*x1 - 12;\n    console.log(`At x = ${x1}: f\'\'(${x1}) = ${f_double_prime_1}`);\n    if (f_double_prime_1 > 0) {\n        console.log("f\'\'(1) > 0, so x = 1 is a local minimum");\n    }\n    \n    let x2 = 3;\n    let f_double_prime_3 = 6*x2 - 12;\n    console.log(`At x = ${x2}: f\'\'(${x2}) = ${f_double_prime_3}`);\n    if (f_double_prime_3 < 0) {\n        console.log("f\'\'(3) < 0, so x = 3 is a local maximum");\n    }\n    \n    // Function values at critical points\n    function f(x) {\n        return x*x*x - 6*x*x + 9*x + 1;\n    }\n    \n    console.log("\\nStep 5: Find function values at critical points");\n    console.log(`f(1) = ${f(1)} (local minimum)`);\n    console.log(`f(3) = ${f(3)} (local maximum)`);\n}\n\nanalyzeFunction();',
              expectedOutput: 'Analyzing f(x) = x³ - 6x² + 9x + 1\n\nStep 1: Find f\'(x)\nf\'(x) = 3x² - 12x + 9\n\nStep 2: Find critical points by solving f\'(x) = 0\n3x² - 12x + 9 = 0\n3(x² - 4x + 3) = 0\n3(x - 1)(x - 3) = 0\nCritical points: x = 1, x = 3\n\nStep 3: Find f\'\'(x) for second derivative test\nf\'\'(x) = 6x - 12\n\nStep 4: Apply second derivative test\nAt x = 1: f\'\'(1) = -6\nf\'\'(1) > 0, so x = 1 is a local minimum\nAt x = 3: f\'\'(3) = 6\nf\'\'(3) < 0, so x = 3 is a local maximum\n\nStep 5: Find function values at critical points\nf(1) = 5 (local minimum)\nf(3) = 1 (local maximum)',
              hints: [
                'Find f\'(x) = 3x² - 12x + 9',
                'Solve f\'(x) = 0 to get x = 1 and x = 3',
                'Find f\'\'(x) = 6x - 12',
                'Test: f\'\'(1) = -6 < 0 (max), f\'\'(3) = 6 > 0 (min)'
              ]
            }
          }
        },
        {
          id: 'lesson-10',
          title: 'Optimization Problems',
          content: 'Master optimization techniques using calculus to solve real-world problems. This lesson covers setting up optimization problems, identifying constraints, and using derivatives to find maximum and minimum values. You will work with applications relevant to computer science and engineering.',
          duration: '120 minutes',
          readingMaterials: [
            'Optimization Problems in Calculus',
            'Constraint Optimization',
            'Real-World Applications of Optimization'
          ],
          keyTerms: [
            'Optimization',
            'Objective Function',
            'Constraint',
            'Maximum Value',
            'Minimum Value',
            'Feasible Region',
            'Boundary Conditions',
            'Applied Mathematics'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Solve this optimization problem: A rectangular computer screen has a perimeter of 60 inches. Find the dimensions that maximize the screen area.',
              initialCode: '// Optimization problem: Maximize screen area with perimeter constraint\n// Constraint: Perimeter = 2(length + width) = 60\n// Objective: Maximize Area = length × width\n\nfunction optimizeScreenArea() {\n    console.log("Optimization Problem: Maximize rectangular screen area");\n    console.log("Constraint: Perimeter = 60 inches\\n");\n    \n    console.log("Step 1: Set up variables");\n    console.log("Let length = x, width = y");\n    console.log("Constraint: 2(x + y) = 60");\n    console.log("Therefore: x + y = 30, so y = 30 - x\\n");\n    \n    console.log("Step 2: Express area as function of one variable");\n    console.log("Area = x × y = x(30 - x) = 30x - x²");\n    console.log("So A(x) = 30x - x²\\n");\n    \n    console.log("Step 3: Find critical points");\n    console.log("A\'(x) = 30 - 2x");\n    console.log("Set A\'(x) = 0: 30 - 2x = 0");\n    console.log("Solving: x = 15\\n");\n    \n    console.log("Step 4: Find corresponding width");\n    let x = 15;\n    let y = 30 - x;\n    console.log(`When x = ${x}, y = 30 - ${x} = ${y}\\n`);\n    \n    console.log("Step 5: Verify it\'s a maximum");\n    console.log("A\'\'(x) = -2 < 0, confirming maximum\\n");\n    \n    let maxArea = x * y;\n    console.log("Step 6: Calculate maximum area");\n    console.log(`Maximum area = ${x} × ${y} = ${maxArea} square inches`);\n    \n    console.log("\\nConclusion: The screen should be 15 × 15 inches (a square)");\n    \n    return maxArea;\n}\n\noptimizeScreenArea();',
              expectedOutput: 'Optimization Problem: Maximize rectangular screen area\nConstraint: Perimeter = 60 inches\n\nStep 1: Set up variables\nLet length = x, width = y\nConstraint: 2(x + y) = 60\nTherefore: x + y = 30, so y = 30 - x\n\nStep 2: Express area as function of one variable\nArea = x × y = x(30 - x) = 30x - x²\nSo A(x) = 30x - x²\n\nStep 3: Find critical points\nA\'(x) = 30 - 2x\nSet A\'(x) = 0: 30 - 2x = 0\nSolving: x = 15\n\nStep 4: Find corresponding width\nWhen x = 15, y = 30 - 15 = 15\n\nStep 5: Verify it\'s a maximum\nA\'\'(x) = -2 < 0, confirming maximum\n\nStep 6: Calculate maximum area\nMaximum area = 15 × 15 = 225 square inches\n\nConclusion: The screen should be 15 × 15 inches (a square)',
              hints: [
                'Use constraint to express one variable in terms of the other',
                'Substitute into area formula to get A(x) = 30x - x²',
                'Find critical point by setting A\'(x) = 30 - 2x = 0',
                'The optimal dimensions are 15 × 15 inches'
              ]
            }
          }
        },
        {
          id: 'lesson-11',
          title: 'Related Rates',
          content: 'Learn to solve problems involving rates of change that are related through geometric or physical relationships. This lesson covers the technique of related rates, implicit differentiation with respect to time, and applications to real-world scenarios including motion and changing quantities.',
          duration: '120 minutes',
          readingMaterials: [
            'Related Rates Problems',
            'Implicit Differentiation with Respect to Time',
            'Applications in Physics and Engineering'
          ],
          keyTerms: [
            'Related Rates',
            'Implicit Differentiation',
            'Rate of Change',
            'Chain Rule Application',
            'Time Derivatives',
            'Physical Applications',
            'Geometric Relationships'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Solve this related rates problem: A balloon is being inflated so its radius increases at 2 cm/min. Find how fast the volume is increasing when the radius is 5 cm.',
              initialCode: '// Related rates: Balloon inflation problem\n// Given: dr/dt = 2 cm/min, find dV/dt when r = 5 cm\n// Volume of sphere: V = (4/3)πr³\n\nfunction balloonInflation() {\n    console.log("Related Rates Problem: Balloon Inflation");\n    console.log("Given: dr/dt = 2 cm/min");\n    console.log("Find: dV/dt when r = 5 cm\\n");\n    \n    console.log("Step 1: Write the volume formula");\n    console.log("V = (4/3)πr³\\n");\n    \n    console.log("Step 2: Differentiate both sides with respect to time");\n    console.log("dV/dt = d/dt[(4/3)πr³]");\n    console.log("dV/dt = (4/3)π · d/dt(r³)");\n    console.log("dV/dt = (4/3)π · 3r² · dr/dt");\n    console.log("dV/dt = 4πr² · dr/dt\\n");\n    \n    console.log("Step 3: Substitute known values");\n    let r = 5; // radius when we want the rate\n    let drdt = 2; // given rate of radius change\n    \n    console.log(`When r = ${r} cm and dr/dt = ${drdt} cm/min:`);\n    console.log(`dV/dt = 4π(${r})² · ${drdt}`);\n    console.log(`dV/dt = 4π(${r*r}) · ${drdt}`);\n    console.log(`dV/dt = ${4 * r * r} · ${drdt} · π`);\n    \n    let dvdt = 4 * Math.PI * r * r * drdt;\n    console.log(`dV/dt = ${4 * r * r * drdt}π cm³/min`);\n    console.log(`dV/dt ≈ ${dvdt.toFixed(2)} cm³/min`);\n    \n    console.log("\\nConclusion: The volume is increasing at 200π ≈ 628.32 cm³/min");\n    \n    return Math.round(dvdt);\n}\n\nballoonInflation();',
              expectedOutput: 'Related Rates Problem: Balloon Inflation\nGiven: dr/dt = 2 cm/min\nFind: dV/dt when r = 5 cm\n\nStep 1: Write the volume formula\nV = (4/3)πr³\n\nStep 2: Differentiate both sides with respect to time\ndV/dt = d/dt[(4/3)πr³]\ndV/dt = (4/3)π · d/dt(r³)\ndV/dt = (4/3)π · 3r² · dr/dt\ndV/dt = 4πr² · dr/dt\n\nStep 3: Substitute known values\nWhen r = 5 cm and dr/dt = 2 cm/min:\ndV/dt = 4π(5)² · 2\ndV/dt = 4π(25) · 2\ndV/dt = 100 · 2 · π\ndV/dt = 200π cm³/min\ndV/dt ≈ 628.32 cm³/min\n\nConclusion: The volume is increasing at 200π ≈ 628.32 cm³/min',
              hints: [
                'Start with V = (4/3)πr³',
                'Differentiate with respect to time: dV/dt = 4πr² · dr/dt',
                'Substitute r = 5 and dr/dt = 2',
                'Calculate: dV/dt = 4π(25)(2) = 200π cm³/min'
              ]
            }
          }
        },
        {
          id: 'lesson-12',
          title: 'Linear Approximation and Differentials',
          content: 'Understand how derivatives provide linear approximations to functions and learn about differentials as a tool for approximating changes. This lesson covers tangent line approximations, the differential concept, and error estimation in numerical computations.',
          duration: '120 minutes',
          readingMaterials: [
            'Linear Approximation and Tangent Lines',
            'Differentials and Error Estimation',
            'Applications in Numerical Methods'
          ],
          keyTerms: [
            'Linear Approximation',
            'Tangent Line Approximation',
            'Differential',
            'Error Estimation',
            'Linearization',
            'Newton\'s Method',
            'Numerical Analysis'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Use linear approximation to estimate √26. Use the fact that √25 = 5 and find the linear approximation of f(x) = √x near x = 25.',
              initialCode: '// Linear approximation: Estimate √26\n// Use f(x) = √x, approximate near x = 25\n// Linear approximation: f(a + h) ≈ f(a) + f\'(a)·h\n\nfunction linearApproximation() {\n    console.log("Linear Approximation: Estimate √26");\n    console.log("Using f(x) = √x near x = 25\\n");\n    \n    console.log("Step 1: Set up the problem");\n    let a = 25;  // point of approximation\n    let target = 26;  // value to estimate\n    let h = target - a;  // change in x\n    \n    console.log(`f(x) = √x`);\n    console.log(`a = ${a} (since √25 = 5 is known)`);\n    console.log(`h = ${target} - ${a} = ${h}`);\n    console.log(`Want to estimate f(${target}) = √${target}\\n`);\n    \n    console.log("Step 2: Find f(a) and f\'(a)");\n    let fa = Math.sqrt(a);  // f(25) = √25 = 5\n    console.log(`f(${a}) = √${a} = ${fa}`);\n    \n    // f\'(x) = 1/(2√x), so f\'(25) = 1/(2√25) = 1/10\n    let fprime_a = 1 / (2 * Math.sqrt(a));\n    console.log(`f\'(x) = 1/(2√x)`);\n    console.log(`f\'(${a}) = 1/(2√${a}) = 1/${2 * fa} = ${fprime_a}\\n`);\n    \n    console.log("Step 3: Apply linear approximation formula");\n    console.log(`f(a + h) ≈ f(a) + f\'(a)·h`);\n    console.log(`f(${target}) ≈ f(${a}) + f\'(${a})·${h}`);\n    console.log(`√${target} ≈ ${fa} + ${fprime_a}·${h}`);\n    console.log(`√${target} ≈ ${fa} + ${fprime_a * h}`);\n    \n    let approximation = fa + fprime_a * h;\n    console.log(`√${target} ≈ ${approximation}\\n`);\n    \n    // Compare with actual value\n    let actual = Math.sqrt(target);\n    let error = Math.abs(actual - approximation);\n    \n    console.log("Step 4: Compare with actual value");\n    console.log(`Approximation: ${approximation}`);\n    console.log(`Actual value: ${actual.toFixed(6)}`);\n    console.log(`Error: ${error.toFixed(6)}`);\n    \n    return approximation;\n}\n\nlinearApproximation();',
              expectedOutput: 'Linear Approximation: Estimate √26\nUsing f(x) = √x near x = 25\n\nStep 1: Set up the problem\nf(x) = √x\na = 25 (since √25 = 5 is known)\nh = 26 - 25 = 1\nWant to estimate f(26) = √26\n\nStep 2: Find f(a) and f\'(a)\nf(25) = √25 = 5\nf\'(x) = 1/(2√x)\nf\'(25) = 1/(2√25) = 1/10 = 0.1\n\nStep 3: Apply linear approximation formula\nf(a + h) ≈ f(a) + f\'(a)·h\nf(26) ≈ f(25) + f\'(25)·1\n√26 ≈ 5 + 0.1·1\n√26 ≈ 5 + 0.1\n√26 ≈ 5.1\n\nStep 4: Compare with actual value\nApproximation: 5.1\nActual value: 5.099020\nError: 0.000980',
              hints: [
                'Use a = 25 since √25 = 5 is known',
                'Find f\'(x) = 1/(2√x), so f\'(25) = 1/10 = 0.1',
                'Apply formula: √26 ≈ √25 + f\'(25)·1 = 5 + 0.1 = 5.1',
                'The approximation 5.1 is very close to the actual value'
              ]
            }
          }
        }
      ],
      assessments: [
        {
          type: 'Optimization Project',
          title: 'Real-World Optimization Problems',
          points: 125,
          dueWeek: 8
        },
        {
          type: 'Application Problems Set',
          title: 'Related Rates and Linear Approximation',
          points: 100,
          dueWeek: 9
        }
      ]
    },
    {
      id: 'module-4',
      title: 'Advanced Differentiation and Transcendental Functions',
      description: 'Extend differentiation techniques to logarithmic, exponential, and trigonometric functions with applications',
      weeks: [10, 11, 12],
      learningOutcomes: [
        'Differentiate exponential and logarithmic functions',
        'Apply differentiation to trigonometric functions',
        'Use implicit differentiation for complex relationships',
        'Solve advanced applications involving transcendental functions'
      ],
      content: [
        {
          id: 'lesson-13',
          title: 'Exponential and Logarithmic Functions',
          content: 'Master differentiation of exponential and logarithmic functions. This lesson covers the derivatives of e^x, a^x, ln(x), and log_a(x), along with applications of these functions in growth and decay models relevant to computer science and data analysis.',
          duration: '120 minutes',
          readingMaterials: [
            'Derivatives of Exponential Functions',
            'Derivatives of Logarithmic Functions',
            'Applications in Growth and Decay Models'
          ],
          keyTerms: [
            'Exponential Function',
            'Natural Logarithm',
            'Base e',
            'Logarithmic Differentiation',
            'Growth Models',
            'Decay Models',
            'Compound Interest',
            'Algorithm Analysis'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Find the derivative of f(x) = x²·e^x using the product rule, then evaluate it at x = 1.',
              initialCode: '// Differentiate f(x) = x²·e^x using product rule\n// Product rule: (uv)\' = u\'v + uv\'\n\nfunction exponentialProductRule() {\n    console.log("Finding derivative of f(x) = x²·e^x");\n    console.log("Using product rule: (uv)\' = u\'v + uv\'\\n");\n    \n    console.log("Step 1: Identify u and v");\n    console.log("Let u = x²");\n    console.log("Let v = e^x\\n");\n    \n    console.log("Step 2: Find derivatives");\n    console.log("u\' = d/dx(x²) = 2x");\n    console.log("v\' = d/dx(e^x) = e^x\\n");\n    \n    console.log("Step 3: Apply product rule");\n    console.log("f\'(x) = u\'v + uv\'");\n    console.log("f\'(x) = (2x)(e^x) + (x²)(e^x)");\n    console.log("f\'(x) = 2x·e^x + x²·e^x");\n    console.log("f\'(x) = e^x(2x + x²)");\n    console.log("f\'(x) = x·e^x(2 + x)\\n");\n    \n    console.log("Step 4: Evaluate at x = 1");\n    let x = 1;\n    let e_power_1 = Math.E;  // e^1 = e\n    let derivative_at_1 = x * e_power_1 * (2 + x);\n    \n    console.log(`f\'(1) = 1·e^1·(2 + 1)`);\n    console.log(`f\'(1) = 1·e·3`);\n    console.log(`f\'(1) = 3e`);\n    console.log(`f\'(1) ≈ ${derivative_at_1.toFixed(4)}`);\n    \n    console.log("\\nFinal answer: f\'(x) = x·e^x(2 + x)");\n    \n    return Math.round(derivative_at_1 * 100) / 100;\n}\n\nexponentialProductRule();',
              expectedOutput: 'Finding derivative of f(x) = x²·e^x\nUsing product rule: (uv)\' = u\'v + uv\'\n\nStep 1: Identify u and v\nLet u = x²\nLet v = e^x\n\nStep 2: Find derivatives\nu\' = d/dx(x²) = 2x\nv\' = d/dx(e^x) = e^x\n\nStep 3: Apply product rule\nf\'(x) = u\'v + uv\'\nf\'(x) = (2x)(e^x) + (x²)(e^x)\nf\'(x) = 2x·e^x + x²·e^x\nf\'(x) = e^x(2x + x²)\nf\'(x) = x·e^x(2 + x)\n\nStep 4: Evaluate at x = 1\nf\'(1) = 1·e^1·(2 + 1)\nf\'(1) = 1·e·3\nf\'(1) = 3e\nf\'(1) ≈ 8.1548\n\nFinal answer: f\'(x) = x·e^x(2 + x)',
              hints: [
                'Use product rule with u = x² and v = e^x',
                'u\' = 2x and v\' = e^x',
                'Apply product rule: f\'(x) = 2x·e^x + x²·e^x',
                'Factor out e^x: f\'(x) = x·e^x(2 + x)'
              ]
            }
          }
        },
        {
          id: 'lesson-14',
          title: 'Trigonometric Functions and Their Derivatives',
          content: 'Learn to differentiate trigonometric functions including sine, cosine, tangent, and their inverses. This lesson covers the derivatives of all six trigonometric functions and their applications in modeling periodic phenomena and wave analysis.',
          duration: '120 minutes',
          readingMaterials: [
            'Derivatives of Trigonometric Functions',
            'Inverse Trigonometric Functions',
            'Applications in Wave Analysis and Signal Processing'
          ],
          keyTerms: [
            'Trigonometric Derivatives',
            'Sine and Cosine',
            'Tangent and Cotangent',
            'Secant and Cosecant',
            'Inverse Trigonometric Functions',
            'Periodic Functions',
            'Wave Analysis'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Find the derivative of f(x) = sin(x)·cos(x) using the product rule, then simplify using trigonometric identities.',
              initialCode: '// Differentiate f(x) = sin(x)·cos(x)\n// Use product rule and trigonometric identities\n\nfunction trigProductRule() {\n    console.log("Finding derivative of f(x) = sin(x)·cos(x)");\n    console.log("Using product rule and trigonometric identities\\n");\n    \n    console.log("Step 1: Apply product rule");\n    console.log("Let u = sin(x), v = cos(x)");\n    console.log("u\' = cos(x), v\' = -sin(x)\\n");\n    \n    console.log("f\'(x) = u\'v + uv\'");\n    console.log("f\'(x) = cos(x)·cos(x) + sin(x)·(-sin(x))");\n    console.log("f\'(x) = cos²(x) - sin²(x)\\n");\n    \n    console.log("Step 2: Simplify using trigonometric identity");\n    console.log("Recall: cos(2x) = cos²(x) - sin²(x)");\n    console.log("Therefore: f\'(x) = cos(2x)\\n");\n    \n    console.log("Alternative approach using double angle identity:");\n    console.log("sin(x)·cos(x) = (1/2)sin(2x)");\n    console.log("So f(x) = (1/2)sin(2x)");\n    console.log("f\'(x) = (1/2)·cos(2x)·2 = cos(2x) ✓\\n");\n    \n    // Test at x = π/4\n    let x = Math.PI / 4;\n    let original_value = Math.sin(x) * Math.cos(x);\n    let derivative_value = Math.cos(2 * x);\n    \n    console.log("Step 3: Verify at x = π/4");\n    console.log(`f(π/4) = sin(π/4)·cos(π/4) = ${original_value.toFixed(4)}`);\n    console.log(`f\'(π/4) = cos(2·π/4) = cos(π/2) = ${derivative_value.toFixed(4)}`);\n    \n    console.log("\\nFinal answer: f\'(x) = cos(2x)");\n    \n    return derivative_value;\n}\n\ntrigProductRule();',
              expectedOutput: 'Finding derivative of f(x) = sin(x)·cos(x)\nUsing product rule and trigonometric identities\n\nStep 1: Apply product rule\nLet u = sin(x), v = cos(x)\nu\' = cos(x), v\' = -sin(x)\n\nf\'(x) = u\'v + uv\'\nf\'(x) = cos(x)·cos(x) + sin(x)·(-sin(x))\nf\'(x) = cos²(x) - sin²(x)\n\nStep 2: Simplify using trigonometric identity\nRecall: cos(2x) = cos²(x) - sin²(x)\nTherefore: f\'(x) = cos(2x)\n\nAlternative approach using double angle identity:\nsin(x)·cos(x) = (1/2)sin(2x)\nSo f(x) = (1/2)sin(2x)\nf\'(x) = (1/2)·cos(2x)·2 = cos(2x) ✓\n\nStep 3: Verify at x = π/4\nf(π/4) = sin(π/4)·cos(π/4) = 0.5000\nf\'(π/4) = cos(2·π/4) = cos(π/2) = 0.0000\n\nFinal answer: f\'(x) = cos(2x)',
              hints: [
                'Apply product rule: u\' = cos(x), v\' = -sin(x)',
                'Get f\'(x) = cos²(x) - sin²(x)',
                'Use identity: cos²(x) - sin²(x) = cos(2x)',
                'Final answer: f\'(x) = cos(2x)'
              ]
            }
          }
        },
        {
          id: 'lesson-15',
          title: 'Implicit Differentiation',
          content: 'Master the technique of implicit differentiation for equations that cannot be easily solved for y as a function of x. This lesson covers the method, applications to related rates, and finding slopes of curves defined implicitly.',
          duration: '120 minutes',
          readingMaterials: [
            'Implicit Differentiation Technique',
            'Applications to Curve Analysis',
            'Related Rates with Implicit Functions'
          ],
          keyTerms: [
            'Implicit Differentiation',
            'Implicit Function',
            'Chain Rule Application',
            'Related Rates',
            'Curve Analysis',
            'Tangent Line to Implicit Curves'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Use implicit differentiation to find dy/dx for the equation x² + y² = 25, then find the slope of the tangent line at the point (3, 4).',
              initialCode: '// Implicit differentiation: x² + y² = 25\n// Find dy/dx and slope at (3, 4)\n\nfunction implicitDifferentiation() {\n    console.log("Implicit Differentiation: x² + y² = 25");\n    console.log("Find dy/dx and slope at point (3, 4)\\n");\n    \n    console.log("Step 1: Differentiate both sides with respect to x");\n    console.log("d/dx(x² + y²) = d/dx(25)");\n    console.log("d/dx(x²) + d/dx(y²) = 0");\n    console.log("2x + d/dx(y²) = 0\\n");\n    \n    console.log("Step 2: Apply chain rule to d/dx(y²)");\n    console.log("d/dx(y²) = 2y · dy/dx");\n    console.log("So: 2x + 2y · dy/dx = 0\\n");\n    \n    console.log("Step 3: Solve for dy/dx");\n    console.log("2y · dy/dx = -2x");\n    console.log("dy/dx = -2x / (2y)");\n    console.log("dy/dx = -x/y\\n");\n    \n    console.log("Step 4: Find slope at point (3, 4)");\n    let x = 3;\n    let y = 4;\n    \n    // Verify point is on curve\n    let verification = x*x + y*y;\n    console.log(`Verify (${x}, ${y}) is on curve: ${x}² + ${y}² = ${verification} ✓\\n`);\n    \n    let slope = -x / y;\n    console.log(`At point (${x}, ${y}):`);\n    console.log(`dy/dx = -x/y = -${x}/${y} = ${slope}`);\n    \n    console.log("\\nConclusion:");\n    console.log("General formula: dy/dx = -x/y");\n    console.log(`Slope at (3, 4): ${slope}`);\n    \n    // Find equation of tangent line\n    // y - y₁ = m(x - x₁)\n    // y - 4 = -3/4(x - 3)\n    console.log(`\\nTangent line equation: y - 4 = ${slope}(x - 3)`);\n    \n    return slope;\n}\n\nimplicitDifferentiation();',
              expectedOutput: 'Implicit Differentiation: x² + y² = 25\nFind dy/dx and slope at point (3, 4)\n\nStep 1: Differentiate both sides with respect to x\nd/dx(x² + y²) = d/dx(25)\nd/dx(x²) + d/dx(y²) = 0\n2x + d/dx(y²) = 0\n\nStep 2: Apply chain rule to d/dx(y²)\nd/dx(y²) = 2y · dy/dx\nSo: 2x + 2y · dy/dx = 0\n\nStep 3: Solve for dy/dx\n2y · dy/dx = -2x\ndy/dx = -2x / (2y)\ndy/dx = -x/y\n\nStep 4: Find slope at point (3, 4)\nVerify (3, 4) is on curve: 3² + 4² = 25 ✓\n\nAt point (3, 4):\ndy/dx = -x/y = -3/4 = -0.75\n\nConclusion:\nGeneral formula: dy/dx = -x/y\nSlope at (3, 4): -0.75\n\nTangent line equation: y - 4 = -0.75(x - 3)',
              hints: [
                'Differentiate both sides: 2x + 2y(dy/dx) = 0',
                'Apply chain rule to y² term',
                'Solve for dy/dx: dy/dx = -x/y',
                'At (3,4): dy/dx = -3/4 = -0.75'
              ]
            }
          }
        },
        {
          id: 'lesson-16',
          title: 'Logarithmic Differentiation',
          content: 'Learn the powerful technique of logarithmic differentiation for complex functions involving products, quotients, and powers. This lesson covers when to use logarithmic differentiation and its applications to functions that are difficult to differentiate directly.',
          duration: '90 minutes',
          readingMaterials: [
            'Logarithmic Differentiation Technique',
            'Applications to Complex Functions',
            'When to Use Logarithmic Differentiation'
          ],
          keyTerms: [
            'Logarithmic Differentiation',
            'Natural Logarithm Properties',
            'Product Rule Alternative',
            'Complex Function Differentiation',
            'Variable Exponents'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Use logarithmic differentiation to find the derivative of f(x) = x^x for x > 0.',
              initialCode: '// Logarithmic differentiation: f(x) = x^x\n// This function has variable base and exponent\n\nfunction logarithmicDifferentiation() {\n    console.log("Logarithmic Differentiation: f(x) = x^x");\n    console.log("Variable base and exponent requires special technique\\n");\n    \n    console.log("Step 1: Take natural logarithm of both sides");\n    console.log("f(x) = x^x");\n    console.log("ln(f(x)) = ln(x^x)");\n    console.log("ln(f(x)) = x · ln(x)\\n");\n    \n    console.log("Step 2: Differentiate both sides with respect to x");\n    console.log("d/dx[ln(f(x))] = d/dx[x · ln(x)]");\n    console.log("(1/f(x)) · f\'(x) = d/dx[x · ln(x)]\\n");\n    \n    console.log("Step 3: Differentiate right side using product rule");\n    console.log("d/dx[x · ln(x)] = (1)·ln(x) + x·(1/x)");\n    console.log("                = ln(x) + 1\\n");\n    \n    console.log("Step 4: Solve for f\'(x)");\n    console.log("(1/f(x)) · f\'(x) = ln(x) + 1");\n    console.log("f\'(x) = f(x) · (ln(x) + 1)");\n    console.log("f\'(x) = x^x · (ln(x) + 1)\\n");\n    \n    console.log("Step 5: Verify at x = e");\n    let x = Math.E;\n    let f_value = Math.pow(x, x);  // e^e\n    let derivative_value = f_value * (Math.log(x) + 1);  // e^e · (ln(e) + 1) = e^e · 2\n    \n    console.log(`At x = e ≈ ${x.toFixed(4)}:`);\n    console.log(`f(e) = e^e ≈ ${f_value.toFixed(4)}`);\n    console.log(`f\'(e) = e^e · (ln(e) + 1) = e^e · (1 + 1) = 2e^e`);\n    console.log(`f\'(e) ≈ ${derivative_value.toFixed(4)}`);\n    \n    console.log("\\nFinal answer: f\'(x) = x^x · (ln(x) + 1)");\n    \n    return Math.round(derivative_value);\n}\n\nlogarithmicDifferentiation();',
              expectedOutput: 'Logarithmic Differentiation: f(x) = x^x\nVariable base and exponent requires special technique\n\nStep 1: Take natural logarithm of both sides\nf(x) = x^x\nln(f(x)) = ln(x^x)\nln(f(x)) = x · ln(x)\n\nStep 2: Differentiate both sides with respect to x\nd/dx[ln(f(x))] = d/dx[x · ln(x)]\n(1/f(x)) · f\'(x) = d/dx[x · ln(x)]\n\nStep 3: Differentiate right side using product rule\nd/dx[x · ln(x)] = (1)·ln(x) + x·(1/x)\n                = ln(x) + 1\n\nStep 4: Solve for f\'(x)\n(1/f(x)) · f\'(x) = ln(x) + 1\nf\'(x) = f(x) · (ln(x) + 1)\nf\'(x) = x^x · (ln(x) + 1)\n\nStep 5: Verify at x = e\nAt x = e ≈ 2.7183:\nf(e) = e^e ≈ 15.1543\nf\'(e) = e^e · (ln(e) + 1) = e^e · (1 + 1) = 2e^e\nf\'(e) ≈ 30.3086\n\nFinal answer: f\'(x) = x^x · (ln(x) + 1)',
              hints: [
                'Take ln of both sides: ln(f(x)) = x·ln(x)',
                'Differentiate: (1/f(x))·f\'(x) = ln(x) + 1',
                'Use product rule on right side',
                'Final answer: f\'(x) = x^x · (ln(x) + 1)'
              ]
            }
          }
        }
      ],
      assessments: [
        {
          type: 'Advanced Techniques Test',
          title: 'Transcendental Function Differentiation',
          points: 100,
          dueWeek: 11
        },
        {
          type: 'Application Project',
          title: 'Modeling with Transcendental Functions',
          points: 125,
          dueWeek: 12
        }
      ]
    },
    {
      id: 'module-5',
      title: 'Integration and Final Applications',
      description: 'Introduction to integration as the inverse of differentiation and final comprehensive applications',
      weeks: [13, 14, 15],
      learningOutcomes: [
        'Understand integration as the inverse of differentiation',
        'Calculate basic integrals and apply fundamental integration techniques',
        'Apply the Fundamental Theorem of Calculus',
        'Solve comprehensive problems integrating all course concepts'
      ],
      content: [
        {
          id: 'lesson-17',
          title: 'Introduction to Integration and Antiderivatives',
          content: 'Discover integration as the reverse process of differentiation. This lesson introduces antiderivatives, indefinite integrals, and basic integration rules. You will learn to recognize antiderivatives and understand the relationship between differentiation and integration.',
          duration: '120 minutes',
          readingMaterials: [
            'Introduction to Integration',
            'Antiderivatives and Indefinite Integrals',
            'Basic Integration Rules'
          ],
          keyTerms: [
            'Antiderivative',
            'Indefinite Integral',
            'Constant of Integration',
            'Integration Rules',
            'Power Rule for Integration',
            'Fundamental Theorem of Calculus'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Find the antiderivative of f(x) = 3x² + 4x - 5 and verify your answer by differentiation.',
              initialCode: '// Find antiderivative and verify by differentiation\n// f(x) = 3x² + 4x - 5\n\nfunction antiderivativeExample() {\n    console.log("Finding antiderivative of f(x) = 3x² + 4x - 5\\n");\n    \n    console.log("Step 1: Apply power rule for integration");\n    console.log("∫(3x² + 4x - 5) dx");\n    console.log("= ∫3x² dx + ∫4x dx + ∫(-5) dx");\n    console.log("= 3∫x² dx + 4∫x dx - 5∫1 dx\\n");\n    \n    console.log("Step 2: Integrate each term");\n    console.log("∫x² dx = x³/3");\n    console.log("∫x dx = x²/2");\n    console.log("∫1 dx = x\\n");\n    \n    console.log("Step 3: Combine results");\n    console.log("F(x) = 3(x³/3) + 4(x²/2) - 5x + C");\n    console.log("F(x) = x³ + 2x² - 5x + C\\n");\n    \n    console.log("Step 4: Verify by differentiation");\n    console.log("F\'(x) = d/dx(x³ + 2x² - 5x + C)");\n    console.log("F\'(x) = 3x² + 4x - 5 + 0");\n    console.log("F\'(x) = 3x² + 4x - 5 ✓\\n");\n    \n    console.log("Verification confirms our antiderivative is correct!");\n    \n    // Test specific values\n    function F(x) {\n        return x*x*x + 2*x*x - 5*x;  // C = 0 for testing\n    }\n    \n    function f(x) {\n        return 3*x*x + 4*x - 5;\n    }\n    \n    console.log("\\nNumerical verification:");\n    let x1 = 2;\n    let x2 = 2.001;\n    let h = x2 - x1;\n    let approximateDerivative = (F(x2) - F(x1)) / h;\n    let exactDerivative = f(x1);\n    \n    console.log(`F\'(${x1}) ≈ (F(${x2}) - F(${x1}))/${h} = ${approximateDerivative.toFixed(3)}`);\n    console.log(`f(${x1}) = ${exactDerivative}`);\n    console.log("Values match, confirming our result ✓");\n    \n    return exactDerivative;\n}\n\nantiderivativeExample();',
              expectedOutput: 'Finding antiderivative of f(x) = 3x² + 4x - 5\n\nStep 1: Apply power rule for integration\n∫(3x² + 4x - 5) dx\n= ∫3x² dx + ∫4x dx + ∫(-5) dx\n= 3∫x² dx + 4∫x dx - 5∫1 dx\n\nStep 2: Integrate each term\n∫x² dx = x³/3\n∫x dx = x²/2\n∫1 dx = x\n\nStep 3: Combine results\nF(x) = 3(x³/3) + 4(x²/2) - 5x + C\nF(x) = x³ + 2x² - 5x + C\n\nStep 4: Verify by differentiation\nF\'(x) = d/dx(x³ + 2x² - 5x + C)\nF\'(x) = 3x² + 4x - 5 + 0\nF\'(x) = 3x² + 4x - 5 ✓\n\nVerification confirms our antiderivative is correct!\n\nNumerical verification:\nF\'(2) ≈ (F(2.001) - F(2))/0.001 = 15.000\nf(2) = 15\nValues match, confirming our result ✓',
              hints: [
                'Use power rule: ∫x^n dx = x^(n+1)/(n+1) + C',
                'For 3x²: ∫3x² dx = 3·x³/3 = x³',
                'For 4x: ∫4x dx = 4·x²/2 = 2x²',
                'Don\'t forget the constant of integration C'
              ]
            }
          }
        },
        {
          id: 'lesson-18',
          title: 'Definite Integrals and the Fundamental Theorem',
          content: 'Learn about definite integrals and the Fundamental Theorem of Calculus. This lesson covers the concept of area under curves, Riemann sums, and how the Fundamental Theorem connects differentiation and integration.',
          duration: '120 minutes',
          readingMaterials: [
            'Definite Integrals and Area Under Curves',
            'The Fundamental Theorem of Calculus',
            'Riemann Sums and Approximation'
          ],
          keyTerms: [
            'Definite Integral',
            'Riemann Sum',
            'Area Under Curve',
            'Fundamental Theorem of Calculus',
            'Limits of Integration',
            'Net Area',
            'Evaluation Theorem'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Calculate the definite integral ∫₁³ (2x + 1) dx using the Fundamental Theorem of Calculus.',
              initialCode: '// Calculate definite integral ∫₁³ (2x + 1) dx\n// Using Fundamental Theorem of Calculus\n\nfunction definiteIntegral() {\n    console.log("Calculating ∫₁³ (2x + 1) dx\\n");\n    \n    console.log("Step 1: Find the antiderivative");\n    console.log("∫(2x + 1) dx = ∫2x dx + ∫1 dx");\n    console.log("              = 2∫x dx + ∫1 dx");\n    console.log("              = 2(x²/2) + x + C");\n    console.log("              = x² + x + C\\n");\n    \n    console.log("Step 2: Apply Fundamental Theorem of Calculus");\n    console.log("∫₁³ (2x + 1) dx = [F(x)]₁³ = F(3) - F(1)");\n    console.log("where F(x) = x² + x\\n");\n    \n    console.log("Step 3: Evaluate at the bounds");\n    \n    // Upper bound: x = 3\n    let x_upper = 3;\n    let F_upper = x_upper * x_upper + x_upper;\n    console.log(`F(3) = 3² + 3 = 9 + 3 = ${F_upper}`);\n    \n    // Lower bound: x = 1\n    let x_lower = 1;\n    let F_lower = x_lower * x_lower + x_lower;\n    console.log(`F(1) = 1² + 1 = 1 + 1 = ${F_lower}`);\n    \n    console.log("\\nStep 4: Calculate the definite integral");\n    let result = F_upper - F_lower;\n    console.log(`∫₁³ (2x + 1) dx = F(3) - F(1) = ${F_upper} - ${F_lower} = ${result}`);\n    \n    console.log("\\nGeometric interpretation:");\n    console.log(`This represents the area under the curve y = 2x + 1`);\n    console.log(`from x = 1 to x = 3, which equals ${result} square units.`);\n    \n    // Verify with Riemann sum approximation\n    console.log("\\nVerification with Riemann sum (using 100 rectangles):");\n    let n = 100;\n    let a = 1, b = 3;\n    let dx = (b - a) / n;\n    let riemannSum = 0;\n    \n    for (let i = 0; i < n; i++) {\n        let x_i = a + i * dx + dx/2;  // midpoint\n        let f_i = 2 * x_i + 1;\n        riemannSum += f_i * dx;\n    }\n    \n    console.log(`Riemann sum approximation: ${riemannSum.toFixed(6)}`);\n    console.log(`Exact value: ${result}`);\n    console.log(`Error: ${Math.abs(result - riemannSum).toFixed(8)}`);\n    \n    return result;\n}\n\ndefiniteIntegral();',
              expectedOutput: 'Calculating ∫₁³ (2x + 1) dx\n\nStep 1: Find the antiderivative\n∫(2x + 1) dx = ∫2x dx + ∫1 dx\n              = 2∫x dx + ∫1 dx\n              = 2(x²/2) + x + C\n              = x² + x + C\n\nStep 2: Apply Fundamental Theorem of Calculus\n∫₁³ (2x + 1) dx = [F(x)]₁³ = F(3) - F(1)\nwhere F(x) = x² + x\n\nStep 3: Evaluate at the bounds\nF(3) = 3² + 3 = 9 + 3 = 12\nF(1) = 1² + 1 = 1 + 1 = 2\n\nStep 4: Calculate the definite integral\n∫₁³ (2x + 1) dx = F(3) - F(1) = 12 - 2 = 10\n\nGeometric interpretation:\nThis represents the area under the curve y = 2x + 1\nfrom x = 1 to x = 3, which equals 10 square units.\n\nVerification with Riemann sum (using 100 rectangles):\nRiemann sum approximation: 10.000000\nExact value: 10\nError: 0.00000000',
              hints: [
                'Find antiderivative: F(x) = x² + x',
                'Apply FTC: ∫₁³ f(x)dx = F(3) - F(1)',
                'Evaluate: F(3) = 12, F(1) = 2',
                'Result: 12 - 2 = 10'
              ]
            }
          }
        },
        {
          id: 'lesson-19',
          title: 'Applications of Integration',
          content: 'Explore practical applications of integration including area calculations, volume computations, and applications to physics and engineering. This lesson connects integration to real-world problems and computer science applications.',
          duration: '120 minutes',
          readingMaterials: [
            'Applications of Integration in Science and Engineering',
            'Area and Volume Calculations',
            'Integration in Computer Science'
          ],
          keyTerms: [
            'Area Between Curves',
            'Volume of Revolution',
            'Physics Applications',
            'Accumulation Functions',
            'Net Change Theorem',
            'Average Value'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Find the area between the curves y = x² and y = 2x from x = 0 to x = 2.',
              initialCode: '// Find area between curves y = x² and y = 2x from x = 0 to x = 2\n// Area = ∫[upper function - lower function] dx\n\nfunction areaBetweenCurves() {\n    console.log("Finding area between y = x² and y = 2x from x = 0 to x = 2\\n");\n    \n    console.log("Step 1: Determine which function is on top");\n    console.log("At x = 0: x² = 0, 2x = 0 (equal)");\n    console.log("At x = 1: x² = 1, 2x = 2 (2x is higher)");\n    console.log("At x = 2: x² = 4, 2x = 4 (equal)");\n    console.log("Between x = 0 and x = 2: 2x ≥ x²\\n");\n    \n    console.log("Step 2: Find intersection points");\n    console.log("Set x² = 2x");\n    console.log("x² - 2x = 0");\n    console.log("x(x - 2) = 0");\n    console.log("x = 0 or x = 2");\n    console.log("Curves intersect at (0,0) and (2,4)\\n");\n    \n    console.log("Step 3: Set up the integral");\n    console.log("Area = ∫₀² (2x - x²) dx");\n    console.log("     = ∫₀² 2x dx - ∫₀² x² dx\\n");\n    \n    console.log("Step 4: Find antiderivatives");\n    console.log("∫2x dx = 2(x²/2) = x²");\n    console.log("∫x² dx = x³/3");\n    console.log("So: ∫(2x - x²) dx = x² - x³/3\\n");\n    \n    console.log("Step 5: Apply Fundamental Theorem");\n    console.log("Area = [x² - x³/3]₀²");\n    console.log("     = (2² - 2³/3) - (0² - 0³/3)");\n    console.log("     = (4 - 8/3) - 0");\n    console.log("     = 4 - 8/3");\n    console.log("     = 12/3 - 8/3");\n    console.log("     = 4/3\\n");\n    \n    let area = 4/3;\n    console.log(`Final answer: Area = ${area.toFixed(4)} square units`);\n    \n    // Verify with numerical integration\n    console.log("\\nVerification with numerical integration:");\n    let n = 1000;\n    let a = 0, b = 2;\n    let dx = (b - a) / n;\n    let numericalArea = 0;\n    \n    for (let i = 0; i < n; i++) {\n        let x = a + i * dx + dx/2;\n        let height = 2*x - x*x;\n        numericalArea += height * dx;\n    }\n    \n    console.log(`Numerical approximation: ${numericalArea.toFixed(6)}`);\n    console.log(`Exact value: ${area.toFixed(6)}`);\n    console.log(`Error: ${Math.abs(area - numericalArea).toFixed(8)}`);\n    \n    return parseFloat(area.toFixed(4));\n}\n\nareaBetweenCurves();',
              expectedOutput: 'Finding area between y = x² and y = 2x from x = 0 to x = 2\n\nStep 1: Determine which function is on top\nAt x = 0: x² = 0, 2x = 0 (equal)\nAt x = 1: x² = 1, 2x = 2 (2x is higher)\nAt x = 2: x² = 4, 2x = 4 (equal)\nBetween x = 0 and x = 2: 2x ≥ x²\n\nStep 2: Find intersection points\nSet x² = 2x\nx² - 2x = 0\nx(x - 2) = 0\nx = 0 or x = 2\nCurves intersect at (0,0) and (2,4)\n\nStep 3: Set up the integral\nArea = ∫₀² (2x - x²) dx\n     = ∫₀² 2x dx - ∫₀² x² dx\n\nStep 4: Find antiderivatives\n∫2x dx = 2(x²/2) = x²\n∫x² dx = x³/3\nSo: ∫(2x - x²) dx = x² - x³/3\n\nStep 5: Apply Fundamental Theorem\nArea = [x² - x³/3]₀²\n     = (2² - 2³/3) - (0² - 0³/3)\n     = (4 - 8/3) - 0\n     = 4 - 8/3\n     = 12/3 - 8/3\n     = 4/3\n\nFinal answer: Area = 1.3333 square units\n\nVerification with numerical integration:\nNumerical approximation: 1.333333\nExact value: 1.333333\nError: 0.00000000',
              hints: [
                'Determine 2x is above x² in the interval [0,2]',
                'Set up integral: ∫₀² (2x - x²) dx',
                'Find antiderivative: x² - x³/3',
                'Evaluate: [4 - 8/3] - [0] = 4/3'
              ]
            }
          }
        },
        {
          id: 'lesson-20',
          title: 'Comprehensive Review and Final Applications',
          content: 'Integrate all course concepts in comprehensive problems that combine limits, derivatives, and integrals. This capstone lesson prepares you for advanced mathematics and demonstrates the power and unity of calculus concepts.',
          duration: '120 minutes',
          readingMaterials: [
            'Comprehensive Calculus Applications',
            'Connecting All Calculus Concepts',
            'Preparation for Advanced Mathematics'
          ],
          keyTerms: [
            'Comprehensive Problem Solving',
            'Integration of Concepts',
            'Advanced Applications',
            'Mathematical Modeling',
            'Calculus in Computer Science'
          ],
          interactiveExercise: {
            type: 'coding-interactive',
            data: {
              instructions: 'Comprehensive problem: Find the critical points of f(x) = x³ - 6x² + 9x + 1, determine their nature, and calculate the area under the curve from x = 0 to x = 3.',
              initialCode: '// Comprehensive calculus problem\n// f(x) = x³ - 6x² + 9x + 1\n// Find critical points, classify them, and find area\n\nfunction comprehensiveProblem() {\n    console.log("Comprehensive Problem: f(x) = x³ - 6x² + 9x + 1\\n");\n    \n    // Part 1: Find critical points\n    console.log("PART 1: Find critical points");\n    console.log("f\'(x) = 3x² - 12x + 9");\n    console.log("Set f\'(x) = 0: 3x² - 12x + 9 = 0");\n    console.log("Divide by 3: x² - 4x + 3 = 0");\n    console.log("Factor: (x - 1)(x - 3) = 0");\n    console.log("Critical points: x = 1, x = 3\\n");\n    \n    // Part 2: Classify critical points\n    console.log("PART 2: Classify critical points using second derivative test");\n    console.log("f\'\'(x) = 6x - 12");\n    \n    let f_double_prime_1 = 6*1 - 12;\n    let f_double_prime_3 = 6*3 - 12;\n    \n    console.log(`f\'\'(1) = 6(1) - 12 = ${f_double_prime_1} < 0 → local maximum`);\n    console.log(`f\'\'(3) = 6(3) - 12 = ${f_double_prime_3} > 0 → local minimum`);\n    \n    // Find function values at critical points\n    function f(x) {\n        return x*x*x - 6*x*x + 9*x + 1;\n    }\n    \n    let f_at_1 = f(1);\n    let f_at_3 = f(3);\n    \n    console.log(`f(1) = 1 - 6 + 9 + 1 = ${f_at_1} (local maximum)`);\n    console.log(`f(3) = 27 - 54 + 27 + 1 = ${f_at_3} (local minimum)\\n`);\n    \n    // Part 3: Calculate area under curve\n    console.log("PART 3: Area under curve from x = 0 to x = 3");\n    console.log("∫₀³ (x³ - 6x² + 9x + 1) dx");\n    \n    console.log("\\nFind antiderivative:");\n    console.log("∫(x³ - 6x² + 9x + 1) dx = x⁴/4 - 6x³/3 + 9x²/2 + x + C");\n    console.log("                          = x⁴/4 - 2x³ + 9x²/2 + x + C\\n");\n    \n    // Evaluate definite integral\n    function F(x) {\n        return x*x*x*x/4 - 2*x*x*x + 9*x*x/2 + x;\n    }\n    \n    let F_3 = F(3);\n    let F_0 = F(0);\n    let area = F_3 - F_0;\n    \n    console.log("Evaluate at bounds:");\n    console.log(`F(3) = 3⁴/4 - 2(3³) + 9(3²)/2 + 3`);\n    console.log(`     = 81/4 - 54 + 81/2 + 3`);\n    console.log(`     = 20.25 - 54 + 40.5 + 3 = ${F_3}`);\n    console.log(`F(0) = 0`);\n    console.log(`Area = F(3) - F(0) = ${F_3} - 0 = ${area}\\n`);\n    \n    console.log("SUMMARY:");\n    console.log(`• Critical points: x = 1 (max, f(1) = ${f_at_1}), x = 3 (min, f(3) = ${f_at_3})`);\n    console.log(`• Area under curve [0,3]: ${area} square units`);\n    \n    // Note about negative area\n    if (area < 0) {\n        console.log(`\\nNote: Negative area indicates net area (curve dips below x-axis)`);\n        console.log(`Absolute area would be ${Math.abs(area)} square units`);\n    }\n    \n    return {\n        criticalPoints: [1, 3],\n        functionValues: [f_at_1, f_at_3],\n        area: area\n    };\n}\n\ncomprehensiveProblem();',
              expectedOutput: 'Comprehensive Problem: f(x) = x³ - 6x² + 9x + 1\n\nPART 1: Find critical points\nf\'(x) = 3x² - 12x + 9\nSet f\'(x) = 0: 3x² - 12x + 9 = 0\nDivide by 3: x² - 4x + 3 = 0\nFactor: (x - 1)(x - 3) = 0\nCritical points: x = 1, x = 3\n\nPART 2: Classify critical points using second derivative test\nf\'\'(x) = 6x - 12\nf\'\'(1) = 6(1) - 12 = -6 < 0 → local maximum\nf\'\'(3) = 6(3) - 12 = 6 > 0 → local minimum\nf(1) = 1 - 6 + 9 + 1 = 5 (local maximum)\nf(3) = 27 - 54 + 27 + 1 = 1 (local minimum)\n\nPART 3: Area under curve from x = 0 to x = 3\n∫₀³ (x³ - 6x² + 9x + 1) dx\n\nFind antiderivative:\n∫(x³ - 6x² + 9x + 1) dx = x⁴/4 - 6x³/3 + 9x²/2 + x + C\n                          = x⁴/4 - 2x³ + 9x²/2 + x + C\n\nEvaluate at bounds:\nF(3) = 3⁴/4 - 2(3³) + 9(3²)/2 + 3\n     = 81/4 - 54 + 81/2 + 3\n     = 20.25 - 54 + 40.5 + 3 = 9.75\nF(0) = 0\nArea = F(3) - F(0) = 9.75 - 0 = 9.75\n\nSUMMARY:\n• Critical points: x = 1 (max, f(1) = 5), x = 3 (min, f(3) = 1)\n• Area under curve [0,3]: 9.75 square units',
              hints: [
                'Find critical points by solving f\'(x) = 3x² - 12x + 9 = 0',
                'Use second derivative test with f\'\'(x) = 6x - 12',
                'For area, integrate f(x) from 0 to 3',
                'Antiderivative is x⁴/4 - 2x³ + 9x²/2 + x'
              ]
            }
          }
        }
      ],
      assessments: [
        {
          type: 'Integration Techniques Test',
          title: 'Fundamental Integration Skills',
          points: 75,
          dueWeek: 14
        },
        {
          type: 'Comprehensive Final Project',
          title: 'Calculus Applications Portfolio',
          points: 150,
          dueWeek: 15
        }
      ]
    }
  ],
  assignments: [
    {
      id: 'assignment-1',
      title: 'Limits and Continuity Analysis Project',
      description: 'Comprehensive analysis of functions using limits and continuity concepts. Students will analyze piecewise functions, find limits graphically and algebraically, and investigate continuity properties.',
      instructions: 'Choose three complex functions including at least one piecewise function. Analyze their limit behavior, continuity, and create detailed graphical representations with explanations.',
      dueWeek: 4,
      points: 100,
      submissionFormat: 'Written report with graphs, calculations, and detailed explanations',
      evaluationCriteria: [
        'Correct limit calculations',
        'Proper continuity analysis',
        'Quality of graphical representations',
        'Mathematical reasoning and explanations',
        'Use of appropriate mathematical notation'
      ]
    },
    {
      id: 'assignment-2',
      title: 'Derivative Applications in Computer Science',
      description: 'Apply derivative concepts to solve problems relevant to computer science including optimization, algorithm analysis, and modeling. Explore connections between calculus and computational thinking.',
      instructions: 'Complete four problems: optimization of data structures, analysis of algorithm efficiency using derivatives, modeling of network growth, and design of user interface elements using calculus principles.',
      dueWeek: 7,
      points: 125,
      submissionFormat: 'Technical report with code examples, mathematical analysis, and practical applications',
      evaluationCriteria: [
        'Correct application of derivative techniques',
        'Quality of optimization solutions',
        'Integration of computer science concepts',
        'Code implementation and testing',
        'Professional presentation and documentation'
      ]
    },
    {
      id: 'assignment-3',
      title: 'Advanced Differentiation Techniques Portfolio',
      description: 'Master advanced differentiation techniques including implicit differentiation, logarithmic differentiation, and related rates. Create a comprehensive portfolio demonstrating proficiency with complex functions.',
      instructions: 'Solve 15 challenging problems covering implicit differentiation, logarithmic differentiation, trigonometric functions, exponential functions, and related rates. Include detailed solution steps and verification.',
      dueWeek: 10,
      points: 150,
      submissionFormat: 'Mathematical portfolio with complete solutions, alternative methods, and error analysis',
      evaluationCriteria: [
        'Accuracy of differentiation techniques',
        'Completeness of solution methods',
        'Quality of mathematical communication',
        'Use of multiple solution approaches',
        'Self-verification and error checking'
      ]
    },
    {
      id: 'assignment-4',
      title: 'Real-World Modeling and Optimization Project',
      description: 'Apply calculus concepts to model and solve real-world problems involving optimization, related rates, and mathematical modeling. Focus on problems relevant to technology and engineering.',
      instructions: 'Choose and solve three substantial real-world problems: one optimization problem, one related rates problem, and one comprehensive modeling project. Include problem analysis, mathematical modeling, solution implementation, and interpretation of results.',
      dueWeek: 13,
      points: 175,
      submissionFormat: 'Professional project report with mathematical analysis, computational implementation, and practical recommendations',
      evaluationCriteria: [
        'Problem identification and analysis',
        'Mathematical modeling accuracy',
        'Solution methodology and implementation',
        'Interpretation and practical application',
        'Professional presentation and communication'
      ]
    }
  ],
  finalProject: {
    id: 'final-project',
    title: 'Comprehensive Calculus Application in Computer Science',
    description: 'Design and implement a comprehensive project that demonstrates mastery of calculus concepts and their applications to computer science. Projects should integrate limits, derivatives, and basic integration concepts.',
    requirements: [
      'Mathematical modeling component using calculus concepts',
      'Computational implementation with code and algorithms',
      'Optimization or analysis component using derivatives',
      'Graphical visualization of mathematical relationships',
      'Comprehensive documentation and mathematical analysis',
      'Presentation demonstrating practical applications'
    ],
    points: 200,
    dueWeek: 15,
    presentationRequired: true,
    projectOptions: [
      'Machine Learning Algorithm Optimization using Calculus',
      'Computer Graphics and Animation with Mathematical Modeling',
      'Network Analysis and Optimization using Calculus Techniques',
      'Financial Technology Application with Mathematical Models',
      'Data Science Project with Statistical and Calculus Foundations',
      'Custom project approved by instructor with significant calculus content'
    ]
  },
  finalExam: {
    id: 'final-exam',
    title: 'Comprehensive Calculus I Examination',
    description: 'Comprehensive examination testing mastery of all calculus concepts covered in the course including limits, derivatives, applications, and basic integration.',
    week: 15,
    points: 200,
    timeLimit: 180,
    format: 'Mixed format: computational problems, conceptual questions, application problems, and proof-based questions',
    sections: [
      {
        title: 'Limits and Continuity',
        points: 50,
        questions: 15
      },
      {
        title: 'Derivatives and Differentiation Techniques',
        points: 60,
        questions: 18
      },
      {
        title: 'Applications of Derivatives',
        points: 50,
        questions: 15
      },
      {
        title: 'Advanced Functions and Differentiation',
        points: 25,
        questions: 8
      },
      {
        title: 'Integration Fundamentals',
        points: 15,
        questions: 6
      }
    ]
  },
  quizzes: [
    {
      id: 'quiz-1',
      title: 'Limits and Limit Laws',
      description: 'Assessment of limit calculation techniques and understanding of continuity',
      week: 3,
      points: 30,
      timeLimit: 45,
      questions: 15,
      topics: ['Limit calculation', 'Limit laws', 'Indeterminate forms', 'Continuity']
    },
    {
      id: 'quiz-2',
      title: 'Basic Differentiation Rules',
      description: 'Test of fundamental differentiation techniques and rules',
      week: 6,
      points: 35,
      timeLimit: 50,
      questions: 18,
      topics: ['Power rule', 'Product rule', 'Quotient rule', 'Chain rule basics']
    },
    {
      id: 'quiz-3',
      title: 'Derivative Applications',
      description: 'Assessment of optimization and related rates problem-solving',
      week: 9,
      points: 40,
      timeLimit: 60,
      questions: 12,
      topics: ['Critical points', 'Optimization', 'Related rates', 'Linear approximation']
    },
    {
      id: 'quiz-4',
      title: 'Advanced Differentiation Techniques',
      description: 'Evaluation of implicit differentiation and transcendental functions',
      week: 12,
      points: 35,
      timeLimit: 50,
      questions: 15,
      topics: ['Implicit differentiation', 'Logarithmic differentiation', 'Exponential functions', 'Trigonometric functions']
    },
    {
      id: 'quiz-5',
      title: 'Integration Fundamentals',
      description: 'Assessment of basic integration techniques and applications',
      week: 14,
      points: 30,
      timeLimit: 45,
      questions: 12,
      topics: ['Antiderivatives', 'Definite integrals', 'Fundamental Theorem of Calculus', 'Area applications']
    }
  ],
  practicalExercises: [
    {
      id: 'exercise-1',
      title: 'Graphical Limit Analysis',
      description: 'Use graphing tools to analyze limit behavior and continuity',
      week: 3,
      points: 25,
      estimatedTime: 90
    },
    {
      id: 'exercise-2',
      title: 'Derivative Calculator Implementation',
      description: 'Program basic derivative calculations using differentiation rules',
      week: 6,
      points: 40,
      estimatedTime: 150
    },
    {
      id: 'exercise-3',
      title: 'Optimization Problem Solver',
      description: 'Develop computational solutions for optimization problems',
      week: 9,
      points: 45,
      estimatedTime: 180
    },
    {
      id: 'exercise-4',
      title: 'Mathematical Modeling Project',
      description: 'Create mathematical models using calculus for real-world scenarios',
      week: 12,
      points: 50,
      estimatedTime: 200
    }
  ],
  resources: [
    {
      category: 'Required Textbooks',
      items: [
        'Stewart, J. (2020). Calculus: Early Transcendentals (8th ed.). Cengage Learning.',
        'Anton, H., Bivens, I., & Davis, S. (2021). Calculus: Early Transcendentals (11th ed.). Wiley.',
        'Larson, R., & Edwards, B. H. (2018). Calculus of a Single Variable (11th ed.). Cengage Learning.'
      ]
    },
    {
      category: 'Online Resources',
      items: [
        'Khan Academy Calculus Course',
        'Paul\'s Online Math Notes - Calculus I',
        'MIT OpenCourseWare - Single Variable Calculus',
        'Wolfram Alpha for computation verification',
        'GeoGebra for graphical analysis'
      ]
    },
    {
      category: 'Mathematical Software',
      items: [
        'Desmos Graphing Calculator',
        'Mathematica or Wolfram Alpha',
        'GeoGebra Classic',
        'Python with NumPy and Matplotlib',
        'MATLAB (if available)'
      ]
    },
    {
      category: 'Reference Materials',
      items: [
        'Calculus Reference Sheet',
        'Table of Derivatives and Integrals',
        'Common Limit Forms',
        'Optimization Problem-Solving Strategies',
        'Integration Techniques Summary'
      ]
    },
    {
      category: 'Practice Resources',
      items: [
        'Calculus Problems and Solutions Database',
        'Online Practice Problem Generators',
        'Past Exam Problems with Solutions',
        'Step-by-step Solution Examples',
        'Video Tutorial Collections'
      ]
    }
  ],
  gradingScale: {
    'A': '93-100%',
    'A-': '90-92%',
    'B+': '87-89%',
    'B': '83-86%',
    'B-': '80-82%',
    'C+': '77-79%',
    'C': '73-76%',
    'C-': '70-72%',
    'D': '60-69%',
    'F': 'Below 60%'
  },
  gradingBreakdown: {
    'Quizzes': '20%',
    'Assignments': '30%',
    'Practical Exercises': '15%',
    'Final Project': '20%',
    'Final Exam': '15%'
  },
  attendancePolicy: 'Regular attendance is crucial for success in calculus. Students are expected to attend all lectures and participate in problem-solving sessions.',
  latePolicy: 'Late assignments will be penalized 15% per day. Mathematical assignments require careful work and cannot be rushed, so plan accordingly.',
  accommodations: 'Students with disabilities may receive extended time for exams, alternative testing formats, and access to mathematical software as needed.',
  academicIntegrity: 'All work must be original. While collaboration on concepts is encouraged, individual assignments must be completed independently. Proper citation required for external resources.',
  technologyRequirements: 'Students need access to graphing calculator or mathematical software, computer for computational assignments, and reliable internet for online resources.',
  mathematicalSoftware: [
    'Graphing calculator (TI-84 or equivalent)',
    'Computer with mathematical software access',
    'Internet access for online mathematical tools'
  ],
  courseEvaluation: 'Course effectiveness evaluated through student feedback, assessment performance analysis, and continuous improvement of mathematical instruction methods.'
};